import { useEffect, useMemo, useRef, useState } from 'react'
import AnimalImage from '../components/AnimalImage.jsx'
import Speaker from '../components/Speaker.jsx'
import { canListen, listenOnce, speak } from '../lib/speech.js'
import { play } from '../lib/sounds.js'
import { searchRoster } from '../../server/match_slug.js'
import { requestAnimal } from '../lib/api.js'
import { track, normalizeQuery } from '../lib/analytics.js'
import { widestWordEm } from '../lib/name_fit.js'
import { metAnimals } from '../lib/collection.js'

/**
 * SPEC screen 2: category tabs, card grid, tap two to lock a matchup,
 * "Surprise me", optional search — plus the microphone button (D-046).
 */
export default function Roster({ roster, age, a, b, heard, onPick, onSurprise, onSpeech, onClearHeard, onFight }) {
  const [tab, setTab] = useState('all')
  const [q, setQ] = useState('')
  const [mic, setMic] = useState({ state: 'idle', message: '' }) // idle | listening | error
  const [asked, setAsked] = useState(null) // name the kid has requested
  // Which animals have been in a battle on this device (D-134). Read once per
  // visit: a battle ends on another screen, and the roster is remounted after.
  const [met] = useState(metAnimals)

  const query = q.trim()
  const bySlug = useMemo(() => new Map(roster.animals.map((x) => [x.slug, x])), [roster])
  const visible = useMemo(() => {
    // searchRoster handles aliases, plurals and near-misses (D-075) and puts
    // the best match first (D-207). A search looks through every tab: the
    // tabs step aside while it runs, so a kid on Big cats who types "horse"
    // gets the horse, not "we don't have horse".
    if (query) return searchRoster(query, roster.animals, { groups: roster.groups })
    return tab === 'all' ? roster.animals : roster.animals.filter((x) => x.category === tab)
  }, [roster, tab, query])

  /**
   * Search mode (D-207). With the keyboard up an iPhone shows about 420px of
   * the page, and the first card started 417px down, under the top bar, the
   * heading, the two ways to pick, the field and the tabs: every result sat
   * under the keyboard. From the first letter until the field is left empty
   * the heading, the two buttons and the tabs step aside, and the results
   * start right under the field.
   *
   * Never on the tap that focuses the field. Moving it mid-tap sends the
   * click to whatever slid under the finger, which puts the keyboard away —
   * the lost tap D-151 fixed. A letter is not a tap, and after ✕ the field
   * keeps its place, because the kid is about to type the next animal.
   */
  const [focused, setFocused] = useState(false)
  const [held, setHeld] = useState(false) // the field has held a query since it took focus
  useEffect(() => { if (focused && query) setHeld(true) }, [focused, query])
  const searching = !!query || (focused && held)

  // Each letter re-ranks the cards and the best one is under the field, so
  // that is where the screen goes — on a phone the screen scrolls, on the
  // iPad the grid does.
  const screenRef = useRef(null)
  const gridRef = useRef(null)
  useEffect(() => {
    if (!query) return
    for (const el of [screenRef.current, gridRef.current]) if (el) el.scrollTop = 0
  }, [query])

  /**
   * Back to every animal (D-208). The picked tab carries a ✕ and a tap on it
   * again lands here, because by the time a kid reaches Farm and home the All
   * tab is scrolled off the strip's left end. The strip goes back to its start
   * so the kid sees All picked.
   */
  const tabsRef = useRef(null)
  const showAll = () => {
    setTab('all')
    const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    tabsRef.current?.scrollTo({ left: 0, behavior: reduce ? 'auto' : 'smooth' })
  }

  /** Three animals to offer when a search finds nothing, stable per query. */
  const suggestions = useMemo(() => {
    let seed = q.length * 7919 + 13
    const rnd = () => ((seed = (seed * 9301 + 49297) % 233280) / 233280)
    const pool = [...roster.animals]
    return Array.from({ length: 3 }, () => pool.splice(Math.floor(rnd() * pool.length), 1)[0]).filter(Boolean)
  }, [q, roster])

  const A = a && bySlug.get(a)
  const B = b && bySlug.get(b)

  // SPEC Section 1: the 2- and 4-year-olds "mostly want to tap pictures and hear
  // roars". Picking a card plays the animal's sound (D-088); unpicking is quiet.
  const pickCard = (x) => {
    if (x.slug !== a && x.slug !== b) play(x.sound)
    onPick(x.slug)
  }

  /**
   * A tap while the search keyboard is up has to count (D-151).
   *
   * The tap used to be eaten: it blurred the field, the phone closed the
   * keyboard, and the screen reflowed under the finger before the tap had
   * become a click — so the first tap only put the keyboard away and the kid
   * had to aim at the card a second time. Holding focus through `pointerdown`
   * means nothing moves while the tap finishes; the click lands on the card the
   * finger is actually on, and only then do we put the keyboard away ourselves.
   *
   * The hold is on `pointerdown` and on `mousedown`: a browser that cancels the
   * first never fires the second, and one that does not reach us through
   * pointer events at all still keeps its focus. Neither cancels the `click` —
   * Pointer Events names `click` an exception, and holding focus through
   * `mousedown` is how every combo box on the web keeps a keyboard open.
   *
   * All three handlers sit on the screen, not on each control, so every button
   * here — a card, a tab, Fight!, a suggestion — gets the same one-tap
   * behaviour. The search row is exempt: tapping into the field, or its ✕, is a
   * kid who is still typing.
   */
  const searchRef = useRef(null)
  const typing = (target) => {
    const field = searchRef.current
    if (!field || document.activeElement !== field) return false
    return !(target instanceof Element && target.closest('.roster__search'))
  }
  const holdKeyboard = (e) => { if (typing(e.target)) e.preventDefault() }
  const dropKeyboard = (e) => { if (typing(e.target)) searchRef.current.blur() }

  // Telemetry: a search is recorded once it has settled for a moment, not per
  // keystroke. Only a search that found NOTHING carries its (normalised) text —
  // that is the "what do kids want that we don't have" signal (docs/analytics.md).
  useEffect(() => {
    if (!query) return undefined
    const results = visible.length
    const t = setTimeout(() => track('search', results === 0 ? { results, query: normalizeQuery(query) } : { results }), 800)
    return () => clearTimeout(t)
  }, [query, visible.length])

  const startListening = async () => {
    track('speech_start')
    setMic({ state: 'listening', message: 'Say two animals…' })
    try {
      const transcript = await listenOnce()
      setMic({ state: 'idle', message: '' })
      onSpeech(transcript)
    } catch (err) {
      const why = err.message
      track('speech_error', { reason: ['denied', 'no-speech', 'unsupported'].includes(why) ? why : 'error' })
      const message =
        why === 'denied' ? 'The microphone is turned off. Ask a grown-up to turn it on.' :
        why === 'no-speech' ? "I didn't hear anything. Try again!" :
        why === 'unsupported' ? 'This browser cannot listen.' : 'Something went wrong. Try again!'
      setMic({ state: 'error', message })
      speak(message)
    }
  }

  return (
    <section
      ref={screenRef}
      className={`screen roster${searching ? ' is-searching' : ''}${query ? ' has-query' : ''}`}
      onPointerDownCapture={holdKeyboard}
      onMouseDownCapture={holdKeyboard}
      onClick={dropKeyboard}
    >
      <div className="roster__head">
        <h1 className="roster__title">{A && B ? 'Ready to rumble!' : A ? 'Now pick a challenger' : 'Pick two animals'}</h1>
        <Speaker text={A && B ? `${A.name} versus ${B.name}. Tap Fight!` : A ? `${A.name}. Now pick a challenger.` : 'Pick two animals.'} auto={age !== '6'} />
        <div className="roster__actions">
          {canListen() && (
            <button className={`btn btn--mic${mic.state === 'listening' ? ' is-listening' : ''}`} onClick={startListening} disabled={mic.state === 'listening'} aria-label="Say two animals">
              🎤 {mic.state === 'listening' ? 'Listening…' : 'Say it'}
            </button>
          )}
          <button className="btn" onClick={onSurprise}>🎲 Surprise me</button>
        </div>
      </div>

      {(mic.message || heard) && (
        <div className={`heard heard--${mic.state === 'error' ? 'error' : 'ok'}`} role="status">
          {heard ? (
            <>
              <span>I heard: <strong>{heard.text}</strong></span>
              <button className="btn btn--small" onClick={onClearHeard}>Not that</button>
            </>
          ) : (
            <span>{mic.message}</span>
          )}
        </div>
      )}

      <div className="picks" aria-live="polite">
        <PickSlot animal={A} side="a" onClear={A ? () => onPick(A.slug) : null} />
        <div className="picks__vs">VS</div>
        <PickSlot animal={B} side="b" onClear={B ? () => onPick(B.slug) : null} />
        <button className="btn btn--fight" disabled={!(A && B)} onClick={onFight}>Fight!</button>
      </div>

      {/* Search sits on its own row, not inside the tab strip: at the end of 12
          horizontally-scrolling tabs it was unreachable on a phone (D-070). */}
      {age === '6' && (
        <div className="roster__search">
          <input
            ref={searchRef}
            className="search"
            type="search"
            enterKeyHint="search"
            placeholder="Search animals…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
            onFocus={() => setFocused(true)}
            onBlur={() => { setFocused(false); setHeld(false) }}
            aria-label="Search animals"
          />
          {/* The clear X lives inside the field (D-130), so it is where a kid
              reaches for it and the field keeps the whole row. Clearing hands
              the caret straight back to the field: a kid taps ✕ to type the
              NEXT animal, and losing the keyboard there is the same lost tap
              this screen just fixed (D-151). So the ✕ holds the keyboard
              through its own press, as a card does, and search mode stays on
              — the field keeps its place for the next animal (D-207). */}
          {q && (
            <button
              className="btn search__clear"
              onPointerDown={(e) => e.preventDefault()}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setHeld(true); setQ(''); searchRef.current?.focus() }}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      )}

      <nav className="tabs" aria-label="Categories" ref={tabsRef}>
        <button className={`tab${tab === 'all' ? ' is-on' : ''}`} aria-pressed={tab === 'all'} onClick={showAll}>All</button>
        {roster.groups.map((g) => {
          const on = tab === g.category
          return (
            <button key={g.category} className={`tab tab--${g.category}${on ? ' is-on' : ''}`} aria-pressed={on} onClick={on ? showAll : () => setTab(g.category)}>
              {shortLabel(g.label)}
            </button>
          )
        })}
      </nav>

      <div className="grid" ref={gridRef}>
        {visible.map((x) => {
          const sel = x.slug === a ? 'a' : x.slug === b ? 'b' : null
          return (
            <button key={x.slug} className={`card card--${x.category}${sel ? ` is-${sel}` : ''}`} style={{ '--name-em': widestWordEm(x.name) }} onClick={() => pickCard(x)} aria-pressed={!!sel} aria-label={x.name}>
              <AnimalImage slug={x.slug} pose="neutral" alt="" lazy />
              <span className="card__name">{x.name}</span>
              {sel && <span className="card__tag">{sel === 'a' ? '1' : '2'}</span>}
              {/* The empty spot on the sticker sheet: a small ring on every animal
                  this device has not battled yet (D-134). Decoration — the
                  collection screen carries the count in words. */}
              {!met.has(x.slug) && <span className="card__unmet" aria-hidden="true" />}
            </button>
          )
        })}
        {!visible.length && (
          <div className="grid__empty">
            {/* SPEC Section 9: Phase 1 has a fixed roster, so an unknown animal
                is a request, not an error. This is the "Can't find your animal?"
                path — request_animal.js has existed since step 6 but nothing
                called it until now (D-075). */}
            <p className="grid__empty-title">
              We don't have {q.trim() ? <strong>{q.trim()}</strong> : 'that one'} yet.
            </p>
            <p className="grid__empty-sub">There are {roster.animals.length} animals to battle. Try one of these:</p>
            <div className="grid__suggest">
              {suggestions.map((s) => (
                <button key={s.slug} className="btn btn--small" onClick={() => { setQ(''); setTab('all'); onPick(s.slug) }}>
                  {s.name}
                </button>
              ))}
            </div>
            {q.trim() && (
              asked === q.trim() ? (
                <p className="grid__asked">Thanks! We'll see about adding {asked}. 🦴</p>
              ) : (
                <button
                  className="btn btn--primary"
                  onClick={() => { const name = q.trim(); requestAnimal(name); track('animal_requested', { name: normalizeQuery(name) }); setAsked(name); speak(`Okay, we will ask for a ${name}.`) }}
                >
                  Ask for {q.trim()}
                </button>
              )
            )}
          </div>
        )}
      </div>
    </section>
  )
}

function PickSlot({ animal, side, onClear }) {
  // --name-em: the widest word's width in em, so the phone's pocket can size the
  // name to its slot the way a card sizes its name (D-116, D-117).
  return (
    <div className={`picks__slot picks__slot--${side}${animal ? ' has-animal' : ''}`} style={animal ? { '--name-em': widestWordEm(animal.name) } : undefined}>
      {animal ? (
        <button className="picks__card" onClick={onClear} aria-label={`Remove ${animal.name}`}>
          <AnimalImage slug={animal.slug} pose="neutral" mirror={side === 'b'} alt="" />
          <span>{animal.name}</span>
        </button>
      ) : (
        <span className="picks__empty">{side === 'a' ? 'first' : 'second'}</span>
      )}
    </div>
  )
}

function shortLabel(label) {
  return label
    .replace('Bears and large mammals', 'Big mammals')
    .replace('Wolves, dogs, mid-size predators', 'Wolves & dogs')
    .replace('Sharks and ocean predators', 'Sharks')
    .replace('Other marine', 'Ocean')
    .replace('Bugs and arachnids', 'Bugs')
    .replace('Odd and fun', 'Odd')
    .replace('Small but deadly', 'Small & deadly')
    .replace('Extinct fan favorites', 'Extinct')
    .replace('Myths and legends', 'Legends')
}
